import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ScrollView, RefreshControl } from 'react-native';
import { Card, Searchbar, Chip, ActivityIndicator, FAB } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const TIPO_CONFIG = {
  penalizacion: { label: 'Penalización', color: '#DC3545', icon: 'remove-circle' },
  bono: { label: 'Bono', color: '#28A745', icon: 'add-circle' },
};

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', color: '#FFC107' },
  aprobada: { label: 'Aprobada', color: '#28A745' },
  cancelada: { label: 'Cancelada', color: '#DC3545' },
};

const PenalizacionesScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [penalizaciones, setPenalizaciones] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const isAdmin = user && ['admin', 'supervisor'].includes(user.rol);

  useEffect(() => {
    loadData();
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [navigation]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPenalizaciones();
      setPenalizaciones(data);
      applyFilters(data, searchQuery, filterTipo);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery, filterTipo]);

  const applyFilters = (data, query, tipo) => {
    let filtered = [...data];
    if (tipo !== 'todos') {
      filtered = filtered.filter(p => p.tipo === tipo);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(p =>
        (p.usuario_nombre || '').toLowerCase().includes(q) ||
        (p.motivo || '').toLowerCase().includes(q)
      );
    }
    setFilteredItems(filtered);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(penalizaciones, query, filterTipo);
  };

  const handleFilterChange = (filter) => {
    setFilterTipo(filter);
    applyFilters(penalizaciones, searchQuery, filter);
  };

  const renderItem = ({ item }) => {
    const tipoConfig = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.penalizacion;
    const estadoConfig = ESTADO_CONFIG[item.estado] || ESTADO_CONFIG.pendiente;

    return (
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.cardHeader}>
            <View style={styles.cardInfo}>
              <View style={styles.tipoRow}>
                <Ionicons name={tipoConfig.icon} size={18} color={tipoConfig.color} />
                <Text style={[styles.tipoLabel, { color: tipoConfig.color }]}>{tipoConfig.label}</Text>
              </View>
              <Text style={styles.cardTitle}>{item.usuario_nombre}</Text>
              <Text style={styles.cardSubtitle}>{item.motivo}</Text>
              {item.venta_id && <Text style={styles.cardSubtitle}>Venta #{item.venta_id}</Text>}
              <Text style={styles.cardDate}>
                {new Date(item.created_at).toLocaleDateString('es-HN')} - Por: {item.aplicado_por_nombre}
              </Text>
            </View>
            <View style={styles.cardRight}>
              <Text style={[styles.cardAmount, { color: tipoConfig.color }]}>
                {item.tipo === 'bono' ? '+' : '-'} L. {parseFloat(item.monto).toFixed(2)}
              </Text>
              <View style={[styles.estadoChip, { backgroundColor: estadoConfig.color }]}>
                <Text style={styles.estadoText}>{estadoConfig.label}</Text>
              </View>
            </View>
          </View>
          {item.notas && <Text style={styles.notasText}>{item.notas}</Text>}
        </Card.Content>
      </Card>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text>Cargando...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Buscar por usuario o motivo..."
        onChangeText={handleSearch}
        value={searchQuery}
        style={styles.searchBar}
      />

      <View style={styles.filtersContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[
            { key: 'todos', label: 'Todos' },
            { key: 'penalizacion', label: 'Penalizaciones' },
            { key: 'bono', label: 'Bonos' },
          ].map(f => (
            <Chip
              key={f.key}
              onPress={() => handleFilterChange(f.key)}
              style={[styles.chip, filterTipo === f.key && styles.chipActive]}
              textStyle={filterTipo === f.key ? styles.chipTextActive : styles.chipTextInactive}
            >
              {f.label}
            </Chip>
          ))}
        </ScrollView>
      </View>

      <FlatList
        data={filteredItems}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />}
        ListEmptyComponent={<Text style={styles.emptyText}>No hay registros</Text>}
        contentContainerStyle={filteredItems.length === 0 ? styles.emptyContainer : undefined}
      />

      {isAdmin && (
        <View style={[styles.fabContainer, { bottom: 16 + insets.bottom }]}>
          <FAB
            style={[styles.fab, { backgroundColor: '#007bff', bottom: 70 }]}
            icon="cog"
            small
            onPress={() => navigation.navigate('ConfigPenalizaciones')}
          />
          <FAB
            style={[styles.fab, { backgroundColor: '#DC3545' }]}
            icon="plus"
            onPress={() => navigation.navigate('NuevaPenalizacion')}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  searchBar: { margin: 8, elevation: 2 },
  filtersContainer: { paddingHorizontal: 8, marginBottom: 8 },
  chip: { marginRight: 6, backgroundColor: '#f0f0f0' },
  chipActive: { backgroundColor: '#4CAF50' },
  chipTextInactive: { color: '#333' },
  chipTextActive: { color: 'white', fontWeight: '600' },
  card: { marginHorizontal: 8, marginBottom: 8, elevation: 2 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  cardInfo: { flex: 1 },
  tipoRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  tipoLabel: { fontSize: 12, fontWeight: '700', marginLeft: 4 },
  cardTitle: { fontSize: 15, fontWeight: 'bold', color: '#333' },
  cardSubtitle: { fontSize: 13, color: '#666', marginTop: 2 },
  cardDate: { fontSize: 11, color: '#999', marginTop: 4 },
  cardRight: { alignItems: 'flex-end' },
  cardAmount: { fontSize: 17, fontWeight: 'bold' },
  estadoChip: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, marginTop: 4 },
  estadoText: { color: '#fff', fontSize: 11, fontWeight: '600' },
  notasText: { fontSize: 12, color: '#666', fontStyle: 'italic', marginTop: 6, borderTopWidth: 1, borderTopColor: '#eee', paddingTop: 6 },
  emptyText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 40 },
  emptyContainer: { flex: 1, justifyContent: 'center' },
  fabContainer: { position: 'absolute', right: 16, bottom: 16 },
  fab: { position: 'absolute', right: 0, bottom: 0 },
});

export default PenalizacionesScreen;
